import React, { useState, useEffect, useRef } from 'react';
import Footer from './footer';
import Header from './header';
import WhatsAppButton from './whatsappButton';
import { useNavigate } from 'react-router-dom';

// --- Hero impact showcase ---
const ImpactCard: React.FC = () => {
  const openWhatsApp = (message: string) =>
    window.open(`https://wa.me/5512987047332?text=${encodeURIComponent(message)}`, '_blank');

  const metrics = [
    { icon: '📈', value: '+40%', label: 'Produtividade média' },
    { icon: '💰', value: 'R$2M+', label: 'Economizados' },
    { icon: '🚀', value: '50+', label: 'Projetos entregues' },
    { icon: '⏱️', value: '99.9%', label: 'Uptime garantido' },
  ];

  const stack = ['React', 'Node.js', 'Python', 'AWS', 'Firebase', 'Flutter', 'OpenAI'];

  return (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 w-full">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-white/60 text-sm font-medium">Impacto real para clientes</span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        {metrics.map(m => (
          <div key={m.value} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="text-xl mb-1">{m.icon}</div>
            <p className="text-white font-bold text-xl">{m.value}</p>
            <p className="text-white/50 text-xs mt-0.5">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="mb-5">
        <p className="text-white/40 text-xs uppercase tracking-widest mb-3">Stack principal</p>
        <div className="flex flex-wrap gap-2">
          {stack.map(t => (
            <span key={t} className="bg-white/10 text-white/70 text-xs px-2.5 py-1 rounded-full border border-white/10">
              {t}
            </span>
          ))}
        </div>
      </div>

      <button
        onClick={() => openWhatsApp('Olá, quero entender o ROI de um projeto com a MainCode.')}
        className="w-full border border-purple-500/40 hover:bg-purple-600/20 text-purple-300 hover:text-purple-200 font-semibold py-3 rounded-xl transition-all text-sm"
      >
        Calcular ROI do seu projeto →
      </button>
    </div>
  );
};

// --- Animated stat counter ---
interface StatCounterProps {
  end: number;
  prefix: string;
  suffix: string;
  label: string;
  start: boolean;
}

const StatCounter: React.FC<StatCounterProps> = ({ end, prefix, suffix, label, start }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const duration = 1800;
    const step = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, start]);

  return (
    <div className="text-center">
      <p className="text-4xl lg:text-5xl font-bold text-[#420C8D]">
        {prefix}{count}{suffix}
      </p>
      <p className="text-gray-600 mt-2 text-sm lg:text-base">{label}</p>
    </div>
  );
};

// --- Service card ---
interface ServiceItemProps {
  icon: string;
  title: string;
  desc: string;
  onClick: () => void;
}

const ServiceItem: React.FC<ServiceItemProps> = ({ icon, title, desc, onClick }) => (
  <div
    onClick={onClick}
    className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-purple-200 hover:shadow-lg hover:shadow-purple-100/50 transition-all group cursor-pointer"
  >
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#420C8D] transition-colors">{title}</h3>
    <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
  </div>
);

// --- Product card ---
interface ProductCardProps {
  name: string;
  tag: string;
  desc: string;
  gradient: string;
  onClick: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ name, tag, desc, gradient, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 border border-white/10 hover:border-white/30 transition-all hover:-translate-y-1 hover:shadow-2xl cursor-pointer`}
  >
    <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4">
      {tag}
    </span>
    <h3 className="text-2xl font-bold text-white mb-2">{name}</h3>
    <p className="text-white/70 text-sm mb-6 leading-relaxed">{desc}</p>
    <span className="text-white/80 font-semibold text-sm hover:text-white transition-colors">
      Saber mais →
    </span>
  </div>
);

// --- Main component ---
const Home: React.FC = () => {
  const navigate = useNavigate();
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  const openWhatsApp = (message: string) =>
    window.open(`https://wa.me/5512987047332?text=${encodeURIComponent(message)}`, '_blank');
  const scrollToId = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.2 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    { icon: '💻', title: 'Desenvolvimento de Software', desc: 'Web, mobile, desktop ou API — construímos o que o seu negócio precisa com tecnologia de ponta.' },
    { icon: '🤖', title: 'Inteligência Artificial', desc: 'Chatbots, automação com IA e machine learning aplicados diretamente ao seu processo de negócio.' },
    { icon: '🏢', title: 'Outsourcing & Consultoria', desc: 'Alocação de profissionais seniores e consultoria estratégica de tecnologia sem custo fixo.' },
    { icon: '☁️', title: 'Infraestrutura', desc: 'Cloud, servidores, segurança e banco de dados com alta disponibilidade e zero downtime.' },
    { icon: '🎨', title: 'Produto & Design', desc: 'Discovery, UX/UI e gestão de qualidade do conceito ao lançamento. Produto certo, no tempo certo.' },
    { icon: '🔧', title: 'Manutenção & Suporte', desc: 'Suporte 24h para manter seus sistemas sempre performando. Contrato de manutenção sem burocracia.' },
  ];

  const products = [
    {
      name: 'Parcelex',
      tag: 'Fintech · Pagamentos',
      desc: 'PIX Parcelado em até 24x sem maquininha. Aumente seu ticket médio em 40% e conversão em 35% já no primeiro mês.',
      gradient: 'from-purple-600 to-purple-900',
    },
    {
      name: 'Sercred',
      tag: 'Benefícios Corporativos',
      desc: 'Plataforma white-label completa de convênios, crédito consignado e benefícios para empresas e cooperativas.',
      gradient: 'from-blue-600 to-blue-900',
    },
    {
      name: 'Agentes de IA',
      tag: 'Inteligência Artificial',
      desc: 'Agentes de IA treinados com o conhecimento do seu negócio. Atendem, qualificam leads e automatizam tarefas 24h.',
      gradient: 'from-violet-600 to-indigo-900',
    },
  ];

  const stats = [
    { end: 2, prefix: 'R$', suffix: 'M+', label: 'Economizados para clientes' },
    { end: 50, prefix: '', suffix: '+', label: 'Projetos entregues' },
    { end: 3, prefix: '', suffix: '+', label: 'Anos de experiência' },
    { end: 7, prefix: '', suffix: '', label: 'Produtos proprietários' },
  ];

  return (
    <div className="flex flex-col w-full bg-[#0A0616] text-white">
      <WhatsAppButton />

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section id="inicio" className="relative min-h-screen flex flex-col bg-gradient-to-br from-[#0A0616] via-[#1A0930] to-[#0D1B3E] overflow-hidden">
        {/* Subtle grid background */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        {/* Glow orbs */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-purple-700/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-blue-700/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col flex-1">
          <div className="pt-5 px-4 lg:px-0">
            <Header />
          </div>

          <div className="flex-1 flex flex-col lg:flex-row items-center justify-center px-6 lg:px-16 py-12 gap-10 lg:gap-16">
            {/* Left: copy */}
            <div className="lg:w-1/2 max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-purple-600/20 border border-purple-500/30 rounded-full px-4 py-2 text-purple-300 text-sm font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
                Software House — São José dos Campos, SP
              </div>
              <h1 className="text-4xl lg:text-6xl font-bold leading-tight mb-6">
                Seu negócio merece{' '}
                <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                  software de verdade.
                </span>
              </h1>
              <p className="text-lg lg:text-xl text-white/70 mb-8 leading-relaxed">
                Desenvolvemos sistemas, apps e produtos digitais que escalam com o seu negócio.
                Mais de <strong className="text-white">R$2 milhões economizados</strong> para clientes em 3+ anos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => openWhatsApp('Olá, vim pelo site e quero saber mais sobre os serviços!')}
                  className="bg-purple-600 hover:bg-purple-500 text-white font-semibold px-8 py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/30 hover:-translate-y-0.5"
                >
                  Falar com especialista →
                </button>
                <button
                  onClick={() => scrollToId('solucoes')}
                  className="border border-white/25 hover:border-white/50 text-white font-semibold px-8 py-4 rounded-xl transition-all hover:bg-white/5"
                >
                  Ver soluções prontas
                </button>
              </div>
              <div className="flex items-center gap-6 mt-8">
                <div className="flex -space-x-2">
                  {['D', 'A', 'M', 'G'].map((l, i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-purple-700 border-2 border-[#1A0930] flex items-center justify-center text-white text-xs font-bold">
                      {l}
                    </div>
                  ))}
                </div>
                <p className="text-white/60 text-sm">
                  <strong className="text-white">50+ empresas</strong> já transformadas
                </p>
              </div>
            </div>

            {/* Right: impact showcase */}
            <div className="lg:w-5/12 w-full max-w-md">
              <ImpactCard />
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────────────────────────────────── */}
      <section id="resultados" ref={statsRef} className="bg-white py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <StatCounter key={i} {...stat} start={statsVisible} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FREE TOOLS ───────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-[#420C8D] to-[#1A0930] py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block bg-white/10 border border-white/20 text-purple-300 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-4">
              100% Gratuito — Sem cadastro
            </span>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
              Ferramentas para o seu negócio
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Descubra o potencial oculto da sua empresa com nossas ferramentas interativas.
              Sem custo, sem compromisso.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div
              onClick={() => openWhatsApp('Olá, quero calcular o ROI de um projeto de software.')}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-purple-400/60 transition-all cursor-pointer hover:-translate-y-1 group"
            >
              <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:bg-purple-500/50 transition-colors">
                📊
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Calculadora de ROI</h3>
              <p className="text-white/60 text-sm mb-4 leading-relaxed">
                Descubra quanto você perde por ano com processos manuais e em quanto tempo um sistema se paga.
              </p>
              <span className="text-purple-300 text-sm font-semibold group-hover:text-purple-200 transition-colors">
                Calcular agora →
              </span>
            </div>

            <div
              onClick={() => openWhatsApp('Olá, quero fazer um diagnóstico digital da minha empresa.')}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-purple-400/60 transition-all cursor-pointer hover:-translate-y-1 group"
            >
              <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:bg-purple-500/50 transition-colors">
                🎯
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Diagnóstico Digital</h3>
              <p className="text-white/60 text-sm mb-4 leading-relaxed">
                5 perguntas que revelam o nível de maturidade digital da sua empresa + plano de ação personalizado.
              </p>
              <span className="text-purple-300 text-sm font-semibold group-hover:text-purple-200 transition-colors">
                Fazer diagnóstico →
              </span>
            </div>

            <div
              onClick={() => openWhatsApp('Olá, quero simular uma solução de pagamentos/PIX parcelado.')}
              className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-purple-400/60 transition-all cursor-pointer hover:-translate-y-1 group"
            >
              <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center text-2xl mb-4 group-hover:bg-purple-500/50 transition-colors">
                💳
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Simulador Parcelex</h3>
              <p className="text-white/60 text-sm mb-4 leading-relaxed">
                Simule o crescimento real das suas vendas com PIX Parcelado em até 24x — sem maquininha.
              </p>
              <span className="text-purple-300 text-sm font-semibold group-hover:text-purple-200 transition-colors">
                Simular vendas →
              </span>
            </div>
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/prospeccao')}
              className="border border-white/25 hover:border-white/50 text-white/80 hover:text-white font-medium px-8 py-3 rounded-xl transition-all hover:bg-white/5 text-sm"
            >
              Abrir ferramenta de prospecção →
            </button>
          </div>
        </div>
      </section>

      {/* ── SERVICES ─────────────────────────────────────────────────────────── */}
      <section id="servicos" className="bg-gray-50 py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#420C8D] text-sm font-semibold uppercase tracking-widest">O que fazemos</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mt-2 mb-4">
              Serviços completos de tecnologia
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Do código à infraestrutura, do produto ao suporte — somos o seu time de tecnologia completo.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <ServiceItem key={i} {...s} onClick={() => openWhatsApp(`Olá, quero saber mais sobre ${s.title}.`)} />
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => openWhatsApp('Olá, quero conhecer os serviços da MainCode.')}
              className="bg-[#420C8D] hover:bg-purple-700 text-white font-semibold px-8 py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-purple-500/25 hover:-translate-y-0.5"
            >
              Conhecer todos os serviços →
            </button>
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ─────────────────────────────────────────────────────────── */}
      <section id="solucoes" className="bg-[#0A0616] py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-purple-300 text-sm font-semibold uppercase tracking-widest">Produtos próprios</span>
            <h2 className="text-3xl lg:text-5xl font-bold text-white mt-2 mb-4">
              Soluções prontas para implementar
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Produtos white-label desenvolvidos pela MainCode, prontos para acelerar o seu negócio.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <ProductCard key={i} {...p} onClick={() => openWhatsApp(`Olá, quero saber mais sobre ${p.name}.`)} />
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => openWhatsApp('Olá, quero conhecer as soluções prontas da MainCode.')}
              className="border border-white/20 hover:border-white/40 text-white/80 hover:text-white font-semibold px-8 py-4 rounded-xl transition-all hover:bg-white/5"
            >
              Ver todas as soluções →
            </button>
          </div>
        </div>
      </section>

      {/* ── WHY MAINCODE ─────────────────────────────────────────────────────── */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="lg:flex items-center gap-16">
            <div className="lg:w-1/2 mb-10 lg:mb-0">
              <span className="text-[#420C8D] text-sm font-semibold uppercase tracking-widest">Por que a MainCode</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-6">
                Tecnologia que multiplica o valor do seu negócio
              </h2>
              <p className="text-gray-500 text-lg mb-8 leading-relaxed">
                Investir em tecnologia certa leva seu negócio ao próximo nível — mais valioso, mais escalável e mais preparado para crescer.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  '✅ Suporte 24h',
                  '✅ Zero Burocracia',
                  '✅ Pagamento Facilitado',
                  '✅ Garantia de Qualidade',
                  '✅ Desenvolvimento Ágil',
                  '✅ Equipe Especializada',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-700 font-medium">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 bg-gradient-to-br from-[#420C8D] to-[#1A0930] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Crie seu app com um dos melhores times de desenvolvimento da América Latina</h3>
              <p className="text-white/70 mb-6">
                Entre em contato com nosso time para dar início ao seu projeto. Resposta em até 24 horas.
              </p>
              <button
                onClick={() => window.open('https://wa.me/5512987047332?text=Olá, quero desenvolver meu projeto!', '_blank')}
                className="bg-white text-[#420C8D] font-bold px-6 py-3 rounded-xl hover:bg-purple-50 transition-all w-full text-center"
              >
                Iniciar projeto agora →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ────────────────────────────────────────────────────────── */}
      <section id="contato" className="bg-gradient-to-br from-[#420C8D] to-[#1A0930] py-16 lg:py-24 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            Pronto para transformar seu negócio?
          </h2>
          <p className="text-white/70 text-lg mb-10">
            Fale com um especialista em até 24h e descubra como a MainCode pode acelerar o seu crescimento — sem enrolação.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => openWhatsApp('Olá, quero falar com um especialista!')}
              className="bg-white text-[#420C8D] font-bold px-10 py-4 rounded-xl hover:bg-purple-50 transition-all hover:shadow-xl hover:-translate-y-0.5"
            >
              Falar pelo WhatsApp →
            </button>
            <button
              onClick={() => openWhatsApp('Olá, quero enviar uma mensagem para a MainCode.')}
              className="border border-white/30 hover:border-white/60 text-white font-semibold px-10 py-4 rounded-xl transition-all hover:bg-white/5"
            >
              Enviar mensagem
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
